import { clone } from "@repo/helpers";
import { DocType, DocTypesEnum } from "@repo/models";
import { DropDownList } from "@repo/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTemplateDataContext } from "../../contexts/templateDataContext";
import DynamicComponent from "../../dynamicComponent";
import { getDerivedValue } from "../../helpers/sectionMapping";
import { MappedComponent } from "../../helpers/types";
import { componentType } from "../../models/enums";
import style from "./style.module.scss";
import { Props } from "./types";

export default function DocumentTypeUpload(props: Props) {
	const { docTypes, component, readOnly } = props;

	const allFiles = component.propertyValue;
	const files = Object.keys(allFiles).filter((file) => allFiles[file]?.length > 0);

	const [selectedDocType, setSelectedDocType] = useState<number>(null);

	useEffect(() => {
		if (selectedDocType) setSelectedDocType(null);
	}, [files.length]);

	const changeHandler = useCallback(async (value: any, id?: string, dontEdit?: boolean) => {
		setSelectedDocType(value);
	}, []);

	return (
		<>
			<DropDownList
				id={"DocumentTypeDropdown"}
				label={"Document Type"}
				values={docTypes}
				value={selectedDocType}
				valueProp={"id"}
				textProp={"value"}
				placeholder={"Select a document type"}
				changeHandler={changeHandler}
				disabled={readOnly}
				clearable
				fullLength
				loaded
			/>
			<DocumentUpload />
			<UploadedDocuments />
		</>
	);

	function DocumentUpload() {
		if (!selectedDocType) return null;

		const docType = docTypes.find((doc) => doc.id === selectedDocType);

		if (!docType) return null;

		const loaAlreadyUploaded = allFiles["loaUploaded"]?.[0]?.alreadyUploaded ?? false;
		const loeAlreadyUploaded = allFiles["loeUploaded"]?.[0]?.alreadyUploaded ?? false;
		const uploaded =
			(docType.id === DocTypesEnum.LOA && loaAlreadyUploaded) ||
			(docType.id === DocTypesEnum.LOE && loeAlreadyUploaded);
		return <DocUpload {...props} docType={docType} uploaded={uploaded} />;
	}

	function UploadedDocuments() {
		const uploadedDocTypes = clone(docTypes)
			.filter(
				(docType) =>
					(files.includes(docType.propName) || files.includes(docType.propName + "Uploaded")) &&
					docType.id !== selectedDocType,
			)
			.sort((a, b) => a.value.localeCompare(b.value));

		return (
			<div className={style.documentUploadContainer}>
				{uploadedDocTypes?.map((docType, index) => (
					<DocUpload {...props} docType={docType} key={index} uploaded />
				))}
			</div>
		);
	}
}

interface DocUploadProps extends Props {
	docType: DocType;
	uploaded?: boolean;
}

function DocUpload(props: DocUploadProps) {
	const { docType, uploaded, component, parentPropertyName, readOnly } = props;
	const { propertyName, extendedProperties, component: baseComponent } = component;
	const { templateData } = useTemplateDataContext();

	const getDerivedValueLocal = useCallback(
		(propertyName: string) => {
			return getDerivedValue({ ...props, ...templateData }, parentPropertyName)(propertyName);
		},
		[props, templateData, parentPropertyName],
	);

	const propName = `${propertyName}.${docType.propName}`;
	const propertyValue = useMemo(() => {
		return {
			values: getDerivedValueLocal(propName),
			uploadedValues: getDerivedValueLocal(propName + "Uploaded"),
		};
	}, [getDerivedValueLocal, propName]);

	const docUploadComponent = useMemo<MappedComponent>(() => {
		return {
			...component,
			type: componentType.fileUpload,
			label: uploaded ? docType.value : `Upload ${docType.value}`,
			linkName: propName,
			propertyName: propName,
			propertyValue: propertyValue,
			disabled: readOnly,
			component: {
				...baseComponent,
				value: "*.*",
			},
			extendedProperties: {
				...extendedProperties,
				maxLength: docType.maxCount ?? extendedProperties.maxLength,
			},
		};
	}, [component, uploaded, docType, propName, readOnly, getDerivedValueLocal]);

	return (
		<DynamicComponent
			{...props}
			component={docUploadComponent}
			parentPropertyName={propertyName}
			readOnly={uploaded || readOnly}
		/>
	);
}
